import { z } from 'zod';
import { ApiError } from '@shared/api/errors';
import { commandBody } from './attachments';
import type {
  RequesterHelpdeskRuntime,
  RequesterBootstrapResponse,
  CustomerTaskListResponse,
  CustomerThread,
  CustomerTask,
} from '../model/runtime';
const option = z.object({ id: z.uuid(), dbValue: z.string(), display: z.string() });
const task = z.object({
  id: z.uuid(),
  taskId: z.string().nullable(),
  title: z.string().nullable(),
  description: z.string().nullable(),
  status: option.nullable(),
  createdAt: z.iso.datetime().nullable(),
  isClosed: z.boolean(),
  author: z.object({ displayName: z.string().nullable() }).nullable(),
}) satisfies z.ZodType<CustomerTask>;
const file = z.object({
  fileRef: z.uuid().nullable(),
  name: z.string(),
  mimeType: z.string().nullable(),
  sizeBytes: z.number().int().nonnegative().nullable(),
  availability: z.enum(['available', 'unavailable']),
});
const meta = z.object({
  page: z.number().int().positive(),
  pageSize: z.number().int().positive(),
  totalItems: z.number().int().nonnegative(),
  totalPages: z.number().int().nonnegative(),
});
const reply = task.extend({
  parentId: z.uuid(),
  kind: z.enum(['support-reply', 'user-reply', 'ai-reply']),
  files: z.array(file),
});
const thread = z.object({
  task: task.extend({ files: z.array(file) }),
  items: z.array(reply),
  meta,
}) satisfies z.ZodType<CustomerThread>;
const bootstrap = z.object({
  data: z.object({
    requester: z.object({ mode: z.enum(['account', 'guest']), email: z.string().nullable() }),
    origin: option,
    feedbackCategories: z.array(option),
  }),
}) satisfies z.ZodType<RequesterBootstrapResponse>;
const list = z.object({ items: z.array(task), meta }) satisfies z.ZodType<CustomerTaskListResponse>;
const create = z
  .object({
    title: z.string().trim().min(1).max(300),
    message: z.string().trim().min(1).max(20000),
    originId: z.uuid(),
    feedbackCategoryId: z.uuid().optional(),
    privacyAcknowledged: z.literal(true),
    requester: z
      .object({ contactEmail: z.email().max(320) })
      .strict()
      .optional(),
  })
  .strict();
export function createRequesterHelpdeskRuntime({
  apiBase,
  source,
  scope,
  expectedAccount = false,
  recoverSession,
  bearer,
  guestOnly = false,
}: {
  apiBase: string;
  source: string;
  scope: string;
  expectedAccount?: boolean;
  recoverSession?: () => Promise<void>;
  bearer?: {
    getAccessToken: () => Promise<string | { token: string; epoch: number }>;
    onRejected: () => void;
    generation: () => number;
    epoch?: () => number;
  };
  guestOnly?: boolean;
}): RequesterHelpdeskRuntime {
  const base = new URL(apiBase);
  if (!['https:', 'http:'].includes(base.protocol)) throw new Error('Invalid Helpdesk API URL');
  const controllers = new Set<AbortController>();
  const lifecycle = { disposed: false };
  async function request(
    path: string,
    options: {
      signal?: AbortSignal;
      body?: unknown;
      key?: string;
      blob?: boolean;
      expectedAccount?: boolean;
      bootstrap?: boolean;
    } = {}
  ): Promise<unknown> {
    if (lifecycle.disposed)
      throw new ApiError({ message: 'Helpdesk session changed', status: 0, code: 'ABORTED' });
    const controller = new AbortController();
    const bearerGeneration = bearer?.generation();
    controllers.add(controller);
    const abort = () => {
      controller.abort();
    };
    options.signal?.addEventListener('abort', abort, { once: true });
    if (options.signal?.aborted) abort();
    const timeoutState = { timedOut: false };
    const timer = setTimeout(() => {
      timeoutState.timedOut = true;
      controller.abort();
    }, 30000);
    const requestAuth: { proofEpoch?: number } = {};
    try {
      const headers: Record<string, string> = { 'X-Helpdesk-Context': 'requester' };
      if (guestOnly) headers['X-Helpdesk-Identity'] = 'guest';
      if (options.key) headers['Idempotency-Key'] = options.key;
      const body =
        options.body instanceof FormData
          ? options.body
          : options.body === undefined
            ? undefined
            : JSON.stringify(options.body);
      if (body !== undefined && !(body instanceof FormData))
        headers['Content-Type'] = 'application/json';
      if (bearer) {
        try {
          const credential = await bearer.getAccessToken();
          const token = typeof credential === 'string' ? credential : credential.token;
          const proofEpoch = typeof credential === 'string' ? undefined : credential.epoch;
          headers.Authorization = `Bearer ${token}`;
          if (proofEpoch !== undefined && bearer.epoch?.() !== proofEpoch)
            throw new DOMException('Aborted', 'AbortError');
          if (bearer.generation() !== bearerGeneration)
            throw new DOMException('Aborted', 'AbortError');
          requestAuth.proofEpoch = proofEpoch;
        } catch {
          throw new ApiError({
            message: 'Helpdesk connection required',
            status: 401,
            code: 'HELPDESK_RECONNECT_REQUIRED',
          });
        }
      }
      controller.signal.throwIfAborted();
      if (bearer && !isBearerCurrent()) throw new DOMException('Aborted', 'AbortError');
      const fetchOptions: RequestInit = {
        method: body === undefined ? 'GET' : 'POST',
        credentials: bearer ? 'omit' : 'include',
        headers,
        body,
        signal: controller.signal,
      };
      const recovery = { attempted: false };
      const accountExpected = options.expectedAccount ?? expectedAccount;
      const recover = async () => {
        if (!accountExpected || !recoverSession || recovery.attempted) return false;
        recovery.attempted = true;
        controller.signal.throwIfAborted();
        await new Promise<void>((resolve, reject) => {
          const onAbort = () => {
            reject(new DOMException('Aborted', 'AbortError'));
          };
          controller.signal.addEventListener('abort', onAbort, { once: true });
          void recoverSession()
            .then(resolve, reject)
            .finally(() => {
              controller.signal.removeEventListener('abort', onAbort);
            });
        });
        controller.signal.throwIfAborted();
        return true;
      };
      let response = await fetch(new URL(path, base), fetchOptions);
      controller.signal.throwIfAborted();
      if (bearer && !isBearerCurrent()) throw new DOMException('Aborted', 'AbortError');
      if (response.status === 401 && bearer) bearer.onRejected();
      if (response.status === 401 && !bearer && (await recover()))
        response = await fetch(new URL(path, base), fetchOptions);
      if (!response.ok) throw await ApiError.fromResponse(response);
      let data: unknown = options.blob ? await response.blob() : await response.json();
      if (
        options.bootstrap &&
        accountExpected &&
        bootstrap.parse(data).data.requester.mode === 'guest'
      ) {
        if (await recover()) {
          response = await fetch(new URL(path, base), fetchOptions);
          if (!response.ok) throw await ApiError.fromResponse(response);
          data = await response.json();
        }
        if (bootstrap.parse(data).data.requester.mode !== 'account') {
          if (bearer && isBearerCurrent()) bearer.onRejected();
          throw new ApiError({
            message: 'Account session is unavailable',
            status: 401,
            code: 'UNAUTHORIZED',
          });
        }
      }
      controller.signal.throwIfAborted();
      if (bearer && !isBearerCurrent()) throw new DOMException('Aborted', 'AbortError');
      return data;
    } catch (error) {
      if (error instanceof ApiError) throw error;
      throw new ApiError({
        message: timeoutState.timedOut
          ? 'Request timed out'
          : controller.signal.aborted
            ? 'Request aborted'
            : 'Request failed',
        status: 0,
        code: timeoutState.timedOut
          ? 'REQUEST_TIMEOUT'
          : controller.signal.aborted
            ? 'ABORTED'
            : 'NETWORK_ERROR',
        cause: error,
      });
    } finally {
      clearTimeout(timer);
      options.signal?.removeEventListener('abort', abort);
      controllers.delete(controller);
    }

    function isBearerCurrent(): boolean {
      return (
        bearer !== undefined &&
        bearer.generation() === bearerGeneration &&
        (requestAuth.proofEpoch === undefined || bearer.epoch?.() === requestAuth.proofEpoch)
      );
    }
  }
  return {
    audience: 'requester',
    scope,
    bootstrap: async (options = {}) =>
      bootstrap.parse(
        await request(
          `/api/v1/tasks/form-options?source=${encodeURIComponent(source)}${options.verifyGuestCookie ? '&verifyGuestCookie=1' : ''}`,
          { ...options, bootstrap: true }
        )
      ),
    list: async ({ page, pageSize = 10, signal }) =>
      list.parse(
        await request(`/api/v1/tasks?page=${String(page)}&pageSize=${String(pageSize)}`, { signal })
      ),
    getThread: async (id, page, signal) =>
      thread.parse(
        await request(
          `/api/v1/tasks/${encodeURIComponent(z.uuid().parse(id))}/replies?page=${String(page)}&pageSize=20`,
          { signal }
        )
      ),
    create: async (input, key, files) =>
      z
        .object({ data: task })
        .parse(
          await request('/api/v1/tasks', { body: commandBody(create.parse(input), files), key })
        ),
    reply: async (id, input, key, files) =>
      z.object({ data: reply }).parse(
        await request(`/api/v1/tasks/${encodeURIComponent(z.uuid().parse(id))}/replies`, {
          body: commandBody(
            z
              .object({ message: z.string().trim().min(1).max(20000) })
              .strict()
              .parse({ message: input.message }),
            files
          ),
          key,
        })
      ),
    getFile: async (id, ref, signal) =>
      z
        .instanceof(Blob)
        .parse(
          await request(
            `/api/v1/tasks/${encodeURIComponent(z.uuid().parse(id))}/files/${encodeURIComponent(z.uuid().parse(ref))}/download`,
            { signal, blob: true }
          )
        ),
    dispose: () => {
      lifecycle.disposed = true;
      for (const controller of controllers) controller.abort();
      controllers.clear();
    },
  };
}
